import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatMoney } from "./utils";
import {
  formatQuantityTotals,
  PeriodDemand,
} from "./demandForecastCalendarUtils";

/**
 * Renders a single period as a stacked bar, with each opportunity segment
 * weighted by probability. Bars rise from the baseline on load (staggered by
 * index) and reveal a tooltip per segment on hover.
 * @param period - The period's aggregated demand and segments.
 * @param maxWeightedRevenue - The tallest period's weighted revenue, for scaling.
 * @param index - The period's position, used to stagger the grow-in animation.
 * @returns The stacked bar column.
 */
export function DemandStackedBar({
  period,
  maxWeightedRevenue,
  index,
}: {
  period: PeriodDemand;
  maxWeightedRevenue: number;
  index: number;
}) {
  const barHeight =
    maxWeightedRevenue > 0
      ? Math.max(
          2,
          Math.round((period.weightedRevenue / maxWeightedRevenue) * 100),
        )
      : 0;

  return (
    <div className="flex min-w-0 flex-col">
      <div className="mb-3 min-h-[44px]">
        <div className="text-sm font-semibold tabular-nums tracking-tight">
          {period.weightedRevenue > 0
            ? formatMoney(period.weightedRevenue)
            : ""}
        </div>
        <div className="truncate text-xs text-muted-foreground">
          {period.weightedRevenue > 0
            ? `${period.segments.length} forecast${
                period.segments.length === 1 ? "" : "s"
              }`
            : ""}
        </div>
      </div>

      <div className="flex h-[280px] items-end px-2">
        {period.weightedRevenue > 0 ? (
          <div
            className="forecast-bar-grow flex w-full flex-col-reverse overflow-hidden rounded-t-md shadow-sm ring-1 ring-black/5"
            style={{
              height: `${barHeight}%`,
              animationDelay: `${Math.min(index * 55, 660)}ms`,
            }}
          >
            {period.segments.map((segment) => {
              const segmentHeight =
                period.weightedRevenue > 0
                  ? Math.max(
                      2,
                      (segment.weightedRevenue / period.weightedRevenue) * 100,
                    )
                  : 0;
              return (
                <Tooltip key={segment.key}>
                  <TooltipTrigger asChild>
                    <div
                      className="w-full border-t border-background/60 first:border-t-0"
                      style={{
                        height: `${segmentHeight}%`,
                        backgroundColor: segment.color,
                      }}
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="font-medium">{segment.label}</div>
                    <div className="tabular-nums text-white/80">
                      {formatMoney(segment.weightedRevenue)} weighted ·{" "}
                      {segment.probability}%
                    </div>
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        ) : (
          <div className="flex h-full w-full items-end justify-center pb-2 text-[11px] text-muted-foreground/70">
            —
          </div>
        )}
      </div>

      <div className="mt-3 text-center text-sm font-medium tracking-tight">
        {period.label}
      </div>
      <div className="mt-0.5 truncate text-center text-xs text-muted-foreground">
        {formatQuantityTotals(period.weightedQuantityTotals)}
      </div>
    </div>
  );
}
